import type { InlineNode, RichText, RichTextNode } from '@diana/contracts';
import { useLayoutEffect, useRef, useState } from 'react';

type CompactRichTextFieldProps = {
  label: string;
  value: RichText;
  variant?: 'compact' | 'extended';
  onChange(value: RichText): void;
};

export function CompactRichTextField({
  label,
  value,
  variant = 'compact',
  onChange,
}: CompactRichTextFieldProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const selectionRef = useRef<Range | null>(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkHref, setLinkHref] = useState('');
  const [linkError, setLinkError] = useState('');

  useLayoutEffect(() => {
    const editor = editorRef.current;
    if (!editor || document.activeElement === editor) return;
    const nextHtml = richTextToHtml(value);
    if (editor.innerHTML !== nextHtml) editor.innerHTML = nextHtml;
  }, [value]);

  const commit = () => {
    const editor = editorRef.current;
    if (!editor) return;
    const next = richTextFromElement(editor);
    if (richTextToHtml(next) !== richTextToHtml(value)) onChange(next);
  };

  const format = (
    command: 'bold' | 'italic' | 'removeFormat' | 'formatBlock' | 'insertUnorderedList' | 'unlink',
    value?: string,
  ) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
    commit();
  };

  const beginLink = () => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
      setLinkError('Select the text that should become a link.');
      setLinkOpen(true);
      return;
    }
    selectionRef.current = selection.getRangeAt(0).cloneRange();
    setLinkError('');
    setLinkOpen(true);
  };

  const applyLink = () => {
    const href = normalizeLinkHref(linkHref);
    if (!href) {
      setLinkError('Use an HTTPS address or an internal site path.');
      return;
    }
    const selection = window.getSelection();
    if (selectionRef.current && selection) {
      selection.removeAllRanges();
      selection.addRange(selectionRef.current);
    }
    editorRef.current?.focus();
    document.execCommand('createLink', false, href);
    commit();
    selectionRef.current = null;
    setLinkHref('');
    setLinkError('');
    setLinkOpen(false);
  };

  return (
    <div className="compact-rich-text">
      <span className="compact-rich-text__label">{label}</span>
      <div className="compact-rich-text__frame">
        <div className="compact-rich-text__toolbar" aria-label={`${label} formatting`}>
          <FormatButton
            label="Bold"
            shortcut="⌘B"
            onClick={() => format('bold')}
            className="compact-rich-text__bold"
          >
            B
          </FormatButton>
          <FormatButton
            label="Italic"
            shortcut="⌘I"
            onClick={() => format('italic')}
            className="compact-rich-text__italic"
          >
            I
          </FormatButton>
          <span className="compact-rich-text__divider" aria-hidden="true" />
          <FormatButton label="Clear formatting" onClick={() => format('removeFormat')}>
            Clear
          </FormatButton>
          {variant === 'extended' ? (
            <>
              <span className="compact-rich-text__divider" aria-hidden="true" />
              <FormatButton
                label="Subheading"
                onClick={() =>
                  format(
                    'formatBlock',
                    document.queryCommandValue('formatBlock') === 'h3' ? 'p' : 'h3',
                  )
                }
              >
                H3
              </FormatButton>
              <FormatButton label="Bullet list" onClick={() => format('insertUnorderedList')}>
                List
              </FormatButton>
              <FormatButton label="Add link" onClick={beginLink}>
                Link
              </FormatButton>
              <FormatButton label="Remove link" onClick={() => format('unlink')}>
                Unlink
              </FormatButton>
            </>
          ) : null}
        </div>
        {variant === 'extended' && linkOpen ? (
          <div className="compact-rich-text__link-panel">
            <label>
              <span>Link destination</span>
              <input
                value={linkHref}
                placeholder="https://… or /about"
                aria-invalid={Boolean(linkError)}
                onChange={(event) => {
                  setLinkHref(event.currentTarget.value);
                  setLinkError('');
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    applyLink();
                  }
                  if (event.key === 'Escape') {
                    setLinkOpen(false);
                    setLinkError('');
                  }
                }}
              />
            </label>
            <div>
              <button type="button" className="button button--quiet" onClick={applyLink}>
                Apply link
              </button>
              <button
                type="button"
                className="text-action"
                onClick={() => {
                  setLinkOpen(false);
                  setLinkError('');
                }}
              >
                Cancel
              </button>
            </div>
            {linkError ? <small role="alert">{linkError}</small> : null}
          </div>
        ) : null}
        <div
          ref={editorRef}
          className="compact-rich-text__surface"
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-label={label}
          aria-multiline="true"
          spellCheck
          onInput={commit}
          onBlur={commit}
          onPaste={(event) => {
            event.preventDefault();
            document.execCommand('insertText', false, event.clipboardData.getData('text/plain'));
            commit();
          }}
        />
      </div>
    </div>
  );
}

function FormatButton({
  label,
  shortcut,
  className,
  children,
  onClick,
}: {
  label: string;
  shortcut?: string;
  className?: string;
  children: string;
  onClick(): void;
}) {
  return (
    <button
      className={['compact-rich-text__button', className].filter(Boolean).join(' ')}
      type="button"
      title={shortcut ? `${label} (${shortcut})` : label}
      aria-label={label}
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

export function richTextToHtml(value: RichText) {
  return value.children
    .map((node) => {
      if (node.type === 'bulletList') {
        return `<ul>${node.items
          .map((item) => `<li>${item.map(inlineNodeToHtml).join('')}</li>`)
          .join('')}</ul>`;
      }
      const tag = node.type === 'h3' ? 'h3' : 'p';
      return `<${tag}>${node.children.map(inlineNodeToHtml).join('') || '<br>'}</${tag}>`;
    })
    .join('');
}

export function richTextFromElement(root: HTMLElement): RichText {
  const children: RichTextNode[] = [];
  let looseInline: InlineNode[] = [];

  const flushLooseInline = () => {
    if (looseInline.length === 0) return;
    children.push({ type: 'paragraph', children: normalizeInlineNodes(looseInline) });
    looseInline = [];
  };

  root.childNodes.forEach((node) => {
    if (!(node instanceof HTMLElement)) {
      looseInline.push(...inlineNodesFromDom(node));
      return;
    }

    const tag = node.tagName.toLowerCase();
    if (tag === 'p' || tag === 'div' || tag === 'h3') {
      flushLooseInline();
      children.push({
        type: tag === 'h3' ? 'h3' : 'paragraph',
        children: normalizeInlineNodes(inlineNodesFromDom(node)),
      });
      return;
    }

    if (tag === 'ul') {
      flushLooseInline();
      const items = Array.from(node.children)
        .filter((item) => item.tagName.toLowerCase() === 'li')
        .map((item) => normalizeInlineNodes(inlineNodesFromDom(item)));
      if (items.length > 0) children.push({ type: 'bulletList', items });
      return;
    }

    if (tag === 'br') {
      flushLooseInline();
      return;
    }

    looseInline.push(...inlineNodesFromDom(node));
  });
  flushLooseInline();

  return {
    type: 'document',
    children:
      children.length > 0
        ? children
        : [{ type: 'paragraph', children: [{ type: 'text', text: '' }] }],
  };
}

function inlineNodeToHtml(node: InlineNode) {
  if (node.type === 'link') {
    return `<a href="${escapeAttribute(node.href)}">${node.children
      .map((child) => escapeHtml(child.text))
      .join('')}</a>`;
  }

  let output = escapeHtml(node.text);
  if (node.marks?.includes('emphasis')) output = `<em>${output}</em>`;
  if (node.marks?.includes('strong')) output = `<strong>${output}</strong>`;
  return output;
}

function inlineNodesFromDom(
  node: Node,
  inheritedMarks: Array<'strong' | 'emphasis'> = [],
): InlineNode[] {
  if (node.nodeType === Node.TEXT_NODE) {
    return [
      {
        type: 'text',
        text: node.textContent ?? '',
        ...(inheritedMarks.length > 0 ? { marks: inheritedMarks } : {}),
      },
    ];
  }
  if (!(node instanceof HTMLElement)) return [];

  const tag = node.tagName.toLowerCase();
  if (tag === 'br') return [{ type: 'text', text: '\n' }];
  if (tag === 'a') {
    const href = node.getAttribute('href');
    if (href && isSupportedHref(href)) {
      return [{ type: 'link', href, children: [{ type: 'text', text: node.textContent ?? '' }] }];
    }
  }

  const marks = [...inheritedMarks];
  if ((tag === 'strong' || tag === 'b') && !marks.includes('strong')) marks.push('strong');
  if ((tag === 'em' || tag === 'i') && !marks.includes('emphasis')) marks.push('emphasis');
  return Array.from(node.childNodes).flatMap((child) => inlineNodesFromDom(child, marks));
}

function normalizeInlineNodes(nodes: InlineNode[]): InlineNode[] {
  const normalized: InlineNode[] = [];
  for (const node of nodes) {
    const previous = normalized.at(-1);
    if (
      node.type === 'text' &&
      previous?.type === 'text' &&
      sameMarks(previous.marks, node.marks)
    ) {
      previous.text += node.text;
    } else {
      normalized.push(node);
    }
  }
  return normalized.length > 0 ? normalized : [{ type: 'text', text: '' }];
}

function sameMarks(
  left: Array<'strong' | 'emphasis'> | undefined,
  right: Array<'strong' | 'emphasis'> | undefined,
) {
  return [...(left ?? [])].sort().join('|') === [...(right ?? [])].sort().join('|');
}

function isSupportedHref(value: string) {
  return (
    /^https:\/\//.test(value) ||
    new Set([
      '/',
      '/about',
      '/portfolio',
      '/pricing',
      '/journal',
      '/gift',
      '/proposals',
      '/weddings',
    ]).has(value)
  );
}

function normalizeLinkHref(value: string) {
  const trimmed = value.trim();
  if (isSupportedHref(trimmed)) return trimmed;
  if (/^[a-z0-9.-]+\.[a-z]{2,}(?:\/.*)?$/i.test(trimmed)) return `https://${trimmed}`;
  return undefined;
}

function escapeHtml(value: string) {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

function escapeAttribute(value: string) {
  return escapeHtml(value);
}
